import React, { PropsWithChildren } from 'react'
import { createContext, useState, useCallback, useContext } from 'react'
import { Confirm, AppLoader, PermissionAlert, NetworkAlert } from './elements'
import { ToastService } from './elements/toaster'
import 'react-toastify/dist/ReactToastify.css'

// evertel
import { useService } from '@evertel/di'
import { ToastContainer, ToastOptions } from 'react-toastify'
import { ThemeState } from '@evertel/web/theme'
import { observer } from 'mobx-react-lite'

type ToggleIntent = 'open' | 'toggle' | 'close'
export interface ConfirmProps {
    title?: string
    message?: string
    isOpen?: boolean
    proceed?: (value?: unknown | undefined) => void
    cancel?: () => void
    cancelButton?: {
        label: string
        color: string
        onClick?: () => void
    }
    acceptButton?: {
        label: string
        color: string
        onClick?: () => void
    }
    headerClassName?: string
    forceChoice?: boolean
}

// Extend react-toastify's ToastOptions with our custom props
export interface EToasterProps extends Omit<ToastOptions, 'type'> {
    message: string
    title?: string
    color?: 'success' | 'danger' | 'warning' | 'info' | 'default'
}

export interface AppLoaderProps {
    name?: string
    type?: 'default' | 'full-overlay'
    isOpen: boolean
    children?: React.ReactNode
}

export interface HeaderAlertProps {
    type: string | undefined
    isOpen: boolean
}

export interface UIContextProps {
    confirm?: ConfirmProps
    setConfirm: (config: ConfirmProps) => any
    isConfirmed: ({ title, message, cancelButton, acceptButton, headerClassName }: IsConfirmedProps) => Promise<boolean>
    toasts?: EToasterProps
    addToast: (config: EToasterProps) => void
    toggleLeftNav: (intent: ToggleIntent) => void
    leftNavOpen?: boolean
    appLoader?: AppLoaderProps
    setAppLoader: (config: AppLoaderProps) => void
    showAppLoader: ({ name, isOpen, type, children }: AppLoaderProps) => void
    alertBar?: HeaderAlertProps
    showAlertBar: ({ type, isOpen }: HeaderAlertProps) => void
    closeAlertBar: () => void
}


export const UIContext = createContext<UIContextProps>({
    setConfirm: (config: ConfirmProps) => {
        //
    },
    addToast: (config: EToasterProps) => {
        //
    },
    toggleLeftNav: (intent: ToggleIntent) => {
        //
    },
    isConfirmed: (config: IsConfirmedProps): any => {
        //
    },
    setAppLoader: (config: AppLoaderProps) => {
        //
    },
    showAppLoader: () => {
        //
    },
    showAlertBar: () => {
        //
    },
    closeAlertBar: () => {
        //
    }
})

type IsConfirmedProps = Omit<ConfirmProps, 'isOpen, proceed, cancel'>

export const UIContextProvider: React.FC<PropsWithChildren> = observer(({
    children
}) => {

    const themeState = useService(ThemeState)
    const [confirm, setConfirm] = useState<ConfirmProps>()
    const [leftNavOpen, setLeftNavOpen] = useState(true)
    const [appLoader, setAppLoader] = useState<AppLoaderProps>()
    const [alertBar, setAlertBar] = useState<HeaderAlertProps>()

    const addToast = useCallback((toastProps: EToasterProps) => {
        const toastService = new ToastService()
        toastService.addToast(toastProps)
    }, [])


    const toggleLeftNav = (intent: 'open' | 'toggle' | 'close' = 'toggle') => {
        // opens/closes the left nav
        if (intent === 'toggle') {
            requestAnimationFrame(() => {
                document.body.classList.toggle('drawer-hide')
            })
            setLeftNavOpen(!leftNavOpen)
        } else if (intent === 'open') {
            requestAnimationFrame(() => {
                document.body.classList.remove('drawer-hide')
            })
            setLeftNavOpen(true)
        } else {
            requestAnimationFrame(() => {
                document.body.classList.add('drawer-hide')
            })
            setLeftNavOpen(false)
        }
    }

    const isConfirmed = ({
        title,
        message,
        cancelButton,
        acceptButton,
        headerClassName,
        forceChoice
    }: IsConfirmedProps): Promise<boolean> => {

        const promise = new Promise((resolve, reject) => {
            setConfirm({
                title, // string
                message, // string
                cancelButton, // {label: '', color: '', ...buttonProps}
                acceptButton, // {label: '', color: '', ...buttonProps}
                headerClassName, // string
                forceChoice, // bool
                isOpen: true, // bool
                proceed: resolve, // func
                cancel: reject // func
            })
        })

        return promise.then(
            () => {
                setConfirm((prevState) => ({
                    ...prevState,
                    isOpen: false
                }))
                return true
            },
            () => {
                setConfirm((prevState) => ({
                    ...prevState,
                    isOpen: false
                }))
                return false
            }
        )
    }

    const showAppLoader = (props: Partial<AppLoaderProps>) => {
        setAppLoader(prevState => {
            // If isOpen is true and wasn't previously true, reset other values
            if (props.isOpen === true && !prevState?.isOpen) {
                return {
                    name: 'page-loading',
                    type: 'default',
                    children: undefined,
                    isOpen: true,
                    ...props
                }
            }

            // Otherwise, preserve existing values
            return {
                ...prevState,
                ...props
            }
        })
    }

    const showAlertBar = ({ type, isOpen }: HeaderAlertProps) => {
        setAlertBar({
            type,
            isOpen
        })
    }

    const closeAlertBar = () => {
        setAlertBar({
            type: '',
            isOpen: false
        })
    }

    return (
        <UIContext.Provider value={{
            confirm,
            setConfirm,
            isConfirmed,
            addToast,
            toggleLeftNav,
            leftNavOpen,
            showAppLoader,
            appLoader,
            setAppLoader,
            showAlertBar,
            alertBar,
            closeAlertBar
        }}>
            <Confirm {...confirm} />
            <AppLoader {...appLoader} />
            <PermissionAlert {...alertBar} />
            <NetworkAlert />
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                // closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={themeState.theme === 'light' ? 'colored' : 'dark'}
            />
            {children}
        </UIContext.Provider>
    )
})

export const UIConsumer = UIContext.Consumer

export const useUI = () => {
    return useContext(UIContext)
}

